import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { EventService } from '@core/services';

import { endpoints } from '@environment';

import {
  UserCreateRequest,
  UserCreateResponseBody,
  UserDetailRequest,
  UserDetailResponseBody,
  UserLookupRequest,
  UserLookupResponseBody,
  UserUpdateRequest,
  UserUpdateResponseBody,
} from '#types/admin';
import { ImageDeleteRequest, ImageDeleteResponseBody, ImageUploadRequest, ImageUploadResponseBody } from '#types/image';

import { CONSTANTS } from '@admin/constants';
import { fromAdmin } from '@admin/state';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  adminUserDetail$ = this.store.select(fromAdmin.selectDetailData);
  adminUserDetailCheckPanelSize$ = new Subject<void>();

  userChangePending$ = new BehaviorSubject<boolean>(false);
  usersGridPanelHeight$ = new BehaviorSubject<number>(0);

  imageDeleted$ = new Subject<void>();
  imageUploaded$ = new Subject<void>();

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private http: HttpClient,
    private store: Store,
    private translocoService: TranslocoService,
    private eventService: EventService
  ) {}

  getImage(type: string) {
    return this.http
      .get(`${endpoints.image.getImage}/${type}`, { observe: 'response', withCredentials: true, responseType: 'blob' })
      .pipe(this.eventService.indicate(CONSTANTS.REQUEST_DOWNLOAD_IMAGE));
  }

  uploadImage(payload: ImageUploadRequest) {
    return this.http.post(endpoints.image.uploadImage, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_UPLOAD_IMAGE),
      map((response: ImageUploadResponseBody) => response)
    );
  }

  deleteImage(payload: ImageDeleteRequest) {
    return this.http.post(endpoints.image.deleteImage, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_DELETE_IMAGE),
      map((response: ImageDeleteResponseBody) => response)
    );
  }

  userLookup(payload: UserLookupRequest) {
    return this.http.post(endpoints.admin.user.user, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_ADMIN_USER_LOOKUP),
      map((response: UserLookupResponseBody) => response)
    );
  }

  userDetail(payload: UserDetailRequest) {
    return this.http.post(endpoints.admin.user.detail, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_USER_DETAIL),
      map((response: UserDetailResponseBody) => response)
    );
  }

  createUser(payload: UserCreateRequest) {
    return this.http.post(endpoints.admin.user.create, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_CREATE_USER),
      map((response: UserCreateResponseBody) => response)
    );
  }

  updateUser(payload: UserUpdateRequest) {
    return this.http.post(endpoints.admin.user.update, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_UPDATE_USER),
      map((response: UserUpdateResponseBody) => response)
    );
  }
}
